import React, {useCallback, useEffect, useRef, useState} from "react";
import {useHistory, useParams} from "react-router-dom";
import Button from "@source/components/UI/buttons/button/button";
import Radio from "@source/components/UI/buttons/radio/radio";
import TextInput from "@source/components/UI/inputs/textInput/textInput";
import ConsentController from "@source/api/controllers/consentController";
import ReqError from "@source/api/reqError";
import {
  ConsentClinicalIdentityVerificationMethod,
  ConsentDocument,
  ConsentDocumentStatus,
  ConsentFamilyContactType,
  ConsentInputValues,
  FAMILY_CONSENT_STATEMENT,
} from "@source/api/models/consentModels";
import {
  canUseFamilyPresentationResponse,
  cleanFamilyMemberInputValues,
  clinicalVerificationMethods,
  createFamilyIdentityRequest,
  createFamilySignRequest,
  familyContactTypes,
  sortConsentInputsByDocument,
} from "@source/api/models/consentHelpers";
import appStore from "@source/store/appStore";
import {ConsentDocumentPreview} from "@source/components/consentDocuments/consentDocumentPreview";
import {ConsentRequestState} from "@source/components/consentDocuments";
import {incompleteStaffInputs} from "../consentSteps/consentStepsShared";
import {TemplateInput} from "../consentSteps/templateInput";
import styles from "./familyPresentation.module.scss";

const NAME_LIMIT = 100;

const requestError = (error: unknown) => {
  const status = error instanceof ReqError ? error.status : undefined;
  if (status === 400) return "This action could not be recorded. Check the details, family fields, and document stage, then try again.";
  if (status === 403) return "You do not have permission to present this consent document.";
  if (status === 404) return "This consent document is no longer available.";
  if (status === 409) return "This document changed stage. Return to the resident and refresh the consent history.";
  return error instanceof Error ? error.message : "Unable to complete this request.";
};

const isTerminalPresentationError = (error: unknown) =>
  error instanceof ReqError
  && (error.status === 403 || error.status === 404 || error.status === 409);

const limitedText = (value: string) => value.slice(0, NAME_LIMIT);

export const FamilyPresentation = ({nursingHomeId}: {nursingHomeId: string}) => {
  const history = useHistory();
  const {residentId, documentId} = useParams<{residentId: string; documentId: string}>();
  const [document, setDocument] = useState<ConsentDocument>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [verified, setVerified] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [relationship, setRelationship] = useState("");
  const [contactType, setContactType] = useState<ConsentFamilyContactType>();
  const [method, setMethod] = useState<ConsentClinicalIdentityVerificationMethod>();
  const [fieldValues, setFieldValues] = useState<ConsentInputValues>({});
  const [previewReady, setPreviewReady] = useState(false);
  const [submitting, setSubmitting] = useState<"identity" | "consent" | "decline" | null>(null);
  const [result, setResult] = useState<"consent" | "decline" | null>(null);
  const mounted = useRef(true);
  const loadRequest = useRef(0);
  const submission = useRef(false);
  const back = useCallback(
    () => history.push(`/home/residents/${residentId}/consent-documents`),
    [history, residentId],
  );

  const load = useCallback(async () => {
    const requestId = ++loadRequest.current;
    setLoading(true); setError("");
    setDocument(undefined); setVerified(false); setFirstName(""); setLastName(""); setRelationship("");
    setContactType(undefined); setMethod(undefined); setFieldValues({}); setPreviewReady(false); setResult(null);
    try {
      const response = await ConsentController.getFamilyPresentation(nursingHomeId, documentId);
      if (!mounted.current || requestId !== loadRequest.current) return;
      if (response.resident._id !== residentId) {
        setError("This consent document belongs to a different resident.");
        return;
      }
      if (!canUseFamilyPresentationResponse(response)) {
        setError("The family member is not the active signer for this document. Return to the resident to review its current signing stage.");
        return;
      }
      setDocument(response);
      setVerified(Boolean(response.familyIdentityVerification));
      setFirstName(response.familyIdentityVerification?.firstName || "");
      setLastName(response.familyIdentityVerification?.lastName || "");
      setRelationship(response.familyIdentityVerification?.relationship || "");
      setContactType(response.familyIdentityVerification?.contactType);
      setFieldValues(response.familyMemberInputValues || {});
    } catch (requestErrorValue) {
      if (mounted.current && requestId === loadRequest.current) setError(requestError(requestErrorValue));
    } finally {
      if (mounted.current && requestId === loadRequest.current) setLoading(false);
    }
  }, [documentId, nursingHomeId, residentId]);

  useEffect(() => {
    mounted.current = true;
    void load();
    return () => {
      mounted.current = false;
      loadRequest.current += 1;
      window.document.body.classList.remove(styles.presentationBody);
    };
  }, [load]);
  useEffect(() => {
    if ((verified || result) && document) window.document.body.classList.add(styles.presentationBody);
    return () => window.document.body.classList.remove(styles.presentationBody);
  }, [document, result, verified]);
  useEffect(() => {
    if (!document || verified || result) return;
    const header = {
      resident: document.resident,
      title: "Present consent to family member",
      subtitle: "Additional step · Staff identity verification",
      usePreviousStepNavigation: true,
      onBack: back,
      onCancel: back,
    };
    appStore.setConsentResidentHeader(header);
    return () => appStore.resetConsentResidentHeader(header);
  }, [back, document, result, verified]);

  if (loading) return <ConsentRequestState variant="loading" title="Loading family member presentation"/>;
  if (error && !document) return <ConsentRequestState variant="error" title="Family member presentation unavailable" description={error} onRetry={() => void load()}/>;
  if (!document) return null;
  const trimmedFirstName = firstName.trim();
  const trimmedLastName = lastName.trim();
  const trimmedRelationship = relationship.trim();
  const displayName = [trimmedFirstName, trimmedLastName].filter(Boolean).join(" ");
  const identityReady = Boolean(trimmedFirstName && trimmedLastName && trimmedRelationship && contactType && method);
  const category = document.categorySnapshot?.name || "Legacy consent document";
  const familyInputs = sortConsentInputsByDocument(
    document.templateSnapshot?.familyMemberInputs || [],
    document.templateSnapshot?.structure?.html,
    "family",
  );
  const incompleteFields = incompleteStaffInputs(familyInputs, fieldValues);

  const verify = async () => {
    if (submission.current || !identityReady || !contactType || !method) return;
    submission.current = true;
    setSubmitting("identity"); setError("");
    try {
      const response = await ConsentController.verifyFamilyIdentity(
        nursingHomeId,
        documentId,
        createFamilyIdentityRequest(trimmedFirstName, trimmedLastName, trimmedRelationship, contactType, method),
      );
      if (!mounted.current) return;
      setDocument(response); setVerified(true);
    } catch (requestErrorValue) {
      if (!mounted.current) return;
      setError(requestError(requestErrorValue));
      if (isTerminalPresentationError(requestErrorValue)) {
        setDocument(undefined);
        setPreviewReady(false);
      }
    } finally {
      submission.current = false;
      if (mounted.current) setSubmitting(null);
    }
  };
  const decide = async (decline = false) => {
    if (submission.current || (!decline && incompleteFields.length)) return;
    submission.current = true;
    setSubmitting(decline ? "decline" : "consent"); setError("");
    try {
      const response = decline
        ? await ConsentController.recordFamilyDecline(nursingHomeId, documentId)
        : await ConsentController.recordFamilyConsent(
          nursingHomeId,
          documentId,
          createFamilySignRequest(cleanFamilyMemberInputValues(familyInputs, fieldValues)),
        );
      if (!mounted.current) return;
      setDocument(response); setResult(decline ? "decline" : "consent");
    } catch (requestErrorValue) {
      if (!mounted.current) return;
      setError(requestError(requestErrorValue));
      if (isTerminalPresentationError(requestErrorValue)) {
        setDocument(undefined);
        setPreviewReady(false);
      }
    } finally {
      submission.current = false;
      if (mounted.current) setSubmitting(null);
    }
  };

  if (!verified) return <div className={styles.start}>
    <section className={styles.identity}>
      <span className={styles.identityStyle}>Staff Identity Verification (Non-Resident)</span>
      <TextInput id="family-signer-first-name" className={styles.nameField} label="First name" value={firstName} handleChange={(event) => setFirstName(limitedText(event.target.value))} disabled={Boolean(submitting)} required/>
      <TextInput id="family-signer-last-name" className={styles.nameField} label="Last name" value={lastName} handleChange={(event) => setLastName(limitedText(event.target.value))} disabled={Boolean(submitting)} required/>
      <TextInput id="family-signer-relationship" className={styles.nameField} label="Relationship to the resident" value={relationship} handleChange={(event) => setRelationship(limitedText(event.target.value))} disabled={Boolean(submitting)} required/>
      <div className={styles.methods}>
        <strong>Contact type</strong>
        {familyContactTypes.map((option) => <Radio key={option.value} name="family-contact-type" value={option.value} checked={contactType === option.value} disabled={Boolean(submitting)} onChange={() => setContactType(option.value)} label={option.label}/>)}
      </div>
      <p className={styles.statement}>I confirm I am with {displayName || "[First name Last name]"} and have verified their identity in person by:</p>
      <div className={styles.methods}>
        {clinicalVerificationMethods.map((option) => <Radio key={option.value} name="family-verification-method" value={option.value} checked={method === option.value} disabled={Boolean(submitting)} onChange={() => setMethod(option.value)} label={option.label}/>)}
      </div>
      {error && <p className={styles.error} role="alert">{error}</p>}
      <div className={styles.actions}>
        <Button variant="outlined" onClick={back}>Present later</Button>
        <Button onClick={() => void verify()} disabled={!identityReady || Boolean(submitting)} isLoading={submitting === "identity"}>Confirm identity</Button>
      </div>
    </section>
  </div>;

  const recordedName = document.familyIdentityVerification?.signerName || displayName || "Family member";
  return <div className={styles.overlay} role="dialog" aria-modal="true" aria-label="Family member consent presentation">
    <header className={styles.contextBar}><span>{`${document.nursingHome?.name || "Consent document"} · ${document.title}`}</span><strong>Family member on site</strong><span>{recordedName}</span></header>
    <main className={styles.presentationMain}>
      {result ? <section className={styles.result}>
        <h2>{result === "consent" ? "Consent recorded" : "Consent declined"}</h2>
        <p>{result === "consent"
          ? (document.status === ConsentDocumentStatus.COMPLETED ? "All required consent steps are complete." : "The signature was recorded and this document is waiting for the remaining required signers.")
          : "The family member declined. This document is now closed."}</p>
        <div className={styles.resultDocument}><strong>{document.title}</strong><span>{category}</span><small>{document.familyIdentityVerification?.identityVerificationStatement || "Family member identity was verified on site."}</small></div>
        <Button className={styles.wideButton} onClick={back}>Return to resident</Button>
      </section> : <section className={styles.presentationPanel}>
        <div className={styles.previewArea}><ConsentDocumentPreview className={styles.presentationPreview} documentId={documentId} onReadinessChange={setPreviewReady}/></div>
        {familyInputs.length ? <div className={styles.fields}>
          {familyInputs.map((input) => <TemplateInput
            key={input.id}
            input={input}
            value={fieldValues[input.id]}
            disabled={Boolean(submitting)}
            onChange={(value) => setFieldValues((current) => ({...current, [input.id]: value}))}
          />)}
          {incompleteFields.length ? <p className={styles.fieldHint}>Complete the family member fields before signing.</p> : null}
        </div> : null}
        {error && <div className={styles.reviewError} role="alert"><p className={styles.error}>{error}</p><Button size="small" variant="outlined" onClick={back}>Return to resident</Button></div>}
        <p className={styles.acceptance}>{FAMILY_CONSENT_STATEMENT}</p>
        <div className={styles.decisionActions}>
          <Button className={styles.wideButton} onClick={() => void decide()} disabled={!previewReady || Boolean(incompleteFields.length) || Boolean(submitting)} isLoading={submitting === "consent"}>Sign Document</Button>
          <Button className={styles.wideButton} variant="outlined" onClick={() => void decide(true)} disabled={!previewReady || Boolean(submitting)} isLoading={submitting === "decline"}>Decline</Button>
        </div>
      </section>}
    </main>
  </div>;
};
